import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  CreateKycDocumentParams,
  CreateUBOParams,
  GetKycDocumentsResult,
  IKycPhoenixApi,
  KYCDocument,
  UBO,
  UBODeclaration,
} from './api';
import { KycPhoenixApi } from './api';

const ApiContext = createContext<IKycPhoenixApi | undefined>(undefined);
const { useApi: useKycPhoenixApi, ApiProvider: KycPhoenixApiProvider } =
  makeApiProvider({
    name: 'KycPhoenixApi',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) => {
      return new KycPhoenixApi(tenantId, auth, apiUrl);
    },
  });

export {
  useKycPhoenixApi,
  KycPhoenixApiProvider,
  ApiContext as KycPhoenixApiContext,
  GetKycDocumentsResult,
  KYCDocument,
  UBO,
  UBODeclaration,
  CreateUBOParams,
};

export const GET_KYC_DOCUMENTS = ['GET_KYC_DOCUMENTS'];

export const useGetKycDocuments = (): UseQueryResult<
  GetKycDocumentsResult,
  unknown
> => {
  return useApiQuery(useKycPhoenixApi, GET_KYC_DOCUMENTS, (api) => {
    return api.getKycDocuments();
  });
};

export const useCreateKycDocument = (): UseMutationResult<
  KYCDocument,
  unknown,
  CreateKycDocumentParams
> => {
  const queryClient = useQueryClient();
  const api = useKycPhoenixApi();
  const result = useMutation<KYCDocument, unknown, CreateKycDocumentParams>({
    mutationKey: ['CREATE_KYC_DOCUMENT'],
    mutationFn: (data) => api.createKycDocument(data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: GET_KYC_DOCUMENTS }),
      ]);
    },
  });
  return result;
};

export const GET_UBO_DECLARATIONS = ['GET_UBO_DECLARATIONS'];
export const GET_UBO_DECLARATION = ['GET_UBO_DECLARATION'];

export const useGetUBODeclarations = (): UseQueryResult<
  {
    ubo_declarations: UBODeclaration[];
  },
  unknown
> => {
  return useApiQuery(useKycPhoenixApi, GET_UBO_DECLARATIONS, (api) => {
    return api.listUBODeclarations();
  });
};

export const useGetUBODeclaration = (
  uboDeclarationId: string | undefined
): UseQueryResult<UBODeclaration, unknown> => {
  return useApiQuery(
    useKycPhoenixApi,
    [GET_UBO_DECLARATION, uboDeclarationId],
    (api) => {
      if (!uboDeclarationId) {
        throw new Error('UBO declaration ID is required');
      }
      return api.getUBODeclaration({
        params: { ubo_declaration_id: uboDeclarationId },
      });
    },
    {
      enabled: !!uboDeclarationId,
    }
  );
};

export const useCreateUBODeclaration = (): UseMutationResult<
  UBODeclaration,
  unknown,
  void
> => {
  const queryClient = useQueryClient();
  const api = useKycPhoenixApi();
  return useMutation({
    mutationKey: ['CREATE_UBO_DECLARATION'],
    mutationFn: () => api.createUBODeclaration({}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_UBO_DECLARATIONS });
    },
  });
};

export const useCreateUBO = (): UseMutationResult<
  UBO,
  unknown,
  { params: { ubo_declaration_id: string } } & { body: CreateUBOParams }
> => {
  const queryClient = useQueryClient();
  const api = useKycPhoenixApi();
  return useMutation({
    mutationKey: ['CREATE_UBO'],
    mutationFn: (params) => api.createUBO(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_UBO_DECLARATION, variables.params.ubo_declaration_id],
      });
      queryClient.invalidateQueries({ queryKey: GET_UBO_DECLARATIONS });
    },
  });
};

export const useSubmitUBODeclaration = (): UseMutationResult<
  UBODeclaration,
  unknown,
  { ubo_declaration_id: string }
> => {
  const queryClient = useQueryClient();
  const api = useKycPhoenixApi();
  return useMutation({
    mutationKey: ['SUBMIT_UBO_DECLARATION'],
    mutationFn: (params) => api.submitUBODeclaration({ params }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_UBO_DECLARATION, variables.ubo_declaration_id],
      });
      queryClient.invalidateQueries({ queryKey: GET_UBO_DECLARATIONS });
    },
  });
};

export const useDeleteUBO = (): UseMutationResult<
  void,
  unknown,
  { ubo_declaration_id: string; ubo_id: string }
> => {
  const queryClient = useQueryClient();
  const api = useKycPhoenixApi();
  return useMutation({
    mutationKey: ['DELETE_UBO'],
    mutationFn: (params) => api.deleteUBO({ params }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [GET_UBO_DECLARATION, variables.ubo_declaration_id],
      });
      queryClient.invalidateQueries({ queryKey: GET_UBO_DECLARATIONS });
    },
  });
};
